/**
 * Collection filters & sorting.
 * Mobile: full-screen drawer from left with Apply/Clear buttons.
 * Desktop: inline sidebar with Apply button, custom sort dropdown.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-filter-form]');
    this.aside = this.querySelector('[data-filters]');
    this.overlay = this.querySelector('[data-filter-overlay]');
    this.label = this.querySelector('[data-filters-label]');
    this.filterArrow = this.querySelector('[data-filter-arrow]');

    this.desktopVisible = false;
    this.lgQuery = window.matchMedia('(min-width: 1024px)');

    // Toggle buttons
    this.querySelectorAll('[data-toggle-filters]').forEach((btn) =>
      btn.addEventListener('click', () => this.toggle())
    );
    this.querySelectorAll('[data-close-filters]').forEach((btn) =>
      btn.addEventListener('click', () => this.closeDrawer())
    );
    this.overlay?.addEventListener('click', () => this.closeDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawerOpen) this.closeDrawer();
    });

    // Price range slider
    this.querySelectorAll('[data-range-slider]').forEach((slider) =>
      this.#initRangeSlider(slider)
    );

    // A bound left at the end of its range has an empty value; dropping those
    // inputs keeps `filter.v.price.gte=` out of the filtered URL.
    this.form?.addEventListener('submit', () => {
      this.querySelectorAll('[data-price-min], [data-price-max]').forEach((input) => {
        if (!input.value) input.disabled = true;
      });
    });

    // Grid column switcher
    this.#initGridSwitcher();

    // Custom sort dropdown
    this.#initSortDropdown();

    // Set initial state
    this.#updateDesktopLabel();
    this.aside?.classList.add('is-desktop-hidden');
  }

  get drawerOpen() {
    return this.aside?.classList.contains('is-open');
  }

  toggle() {
    if (this.lgQuery.matches) {
      this.desktopVisible = !this.desktopVisible;
      this.aside.classList.toggle('is-desktop-hidden');
      this.#updateDesktopLabel();
      // Switch grid cols when filters shown/hidden
      this.#adjustGridForFilters();
    } else {
      this.drawerOpen ? this.closeDrawer() : this.openDrawer();
    }
  }

  openDrawer() {
    this.aside?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.aside?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  #updateDesktopLabel() {
    if (this.label) {
      this.label.textContent = this.desktopVisible ? 'Hide Filters' : 'Show Filters';
    }
    if (this.filterArrow) {
      this.filterArrow.style.transform = this.desktopVisible ? 'rotate(180deg)' : '';
    }
  }

  #adjustGridForFilters() {
    const grid = this.querySelector('[data-product-grid]');
    if (!grid || !this.lgQuery.matches) return;
    if (this.desktopVisible) {
      grid.classList.remove('lg:grid-cols-4');
      grid.classList.add('lg:grid-cols-3');
    } else {
      grid.classList.remove('lg:grid-cols-3');
      grid.classList.add('lg:grid-cols-4');
    }
  }

  // ── Grid switcher ──

  // Desktop and mobile keep separate preferences: picking 3 columns on a laptop
  // shouldn't decide what a phone shows (and vice versa). Mobile defaults to 2.
  #gridStorageKey(isDesktop) {
    return isDesktop ? 'collection-grid-cols' : 'collection-grid-cols-mobile';
  }

  #initGridSwitcher() {
    const grid = this.querySelector('[data-product-grid]');
    const btns = this.querySelectorAll('[data-grid]');
    if (!grid || !btns.length) return;

    const applySaved = () => {
      const isDesktop = this.lgQuery.matches;
      const saved = localStorage.getItem(this.#gridStorageKey(isDesktop));
      if (saved) {
        this.#setGrid(grid, btns, saved);
      } else if (!isDesktop) {
        this.#setGrid(grid, btns, '2');
      }
    };

    applySaved();
    this.lgQuery.addEventListener('change', applySaved);

    btns.forEach((btn) =>
      btn.addEventListener('click', () => {
        const cols = btn.dataset.grid;
        this.#setGrid(grid, btns, cols);
        localStorage.setItem(this.#gridStorageKey(this.lgQuery.matches), cols);
      })
    );
  }

  #setGrid(grid, btns, cols) {
    const isDesktop = this.lgQuery.matches;
    const scope = isDesktop ? 'desktop' : 'mobile';

    if (isDesktop) {
      grid.classList.remove('lg:grid-cols-3', 'lg:grid-cols-4');
      grid.classList.add(`lg:grid-cols-${cols}`);
    } else {
      grid.classList.remove('grid-cols-1', 'grid-cols-2');
      grid.classList.add(`grid-cols-${cols}`);
    }

    btns.forEach((b) => {
      if (b.dataset.gridScope !== scope) return;
      const bars = b.querySelectorAll('[data-col-bar]');
      const isActive = b.dataset.grid === cols;
      bars.forEach((bar) => {
        if (isActive) {
          bar.setAttribute('fill', 'white');
          bar.removeAttribute('stroke');
        } else {
          bar.setAttribute('fill', 'none');
          bar.setAttribute('stroke', 'white');
          bar.setAttribute('stroke-width', '1');
        }
      });
    });
  }

  // ── Custom sort dropdown ──

  #initSortDropdown() {
    const wrapper = this.querySelector('[data-sort-wrapper]');
    const toggle = this.querySelector('[data-sort-toggle]');
    const dropdown = this.querySelector('[data-sort-dropdown]');
    const input = this.querySelector('[data-sort-input]');
    const label = this.querySelector('[data-sort-label]');
    if (!wrapper || !toggle || !dropdown) return;

    toggle.addEventListener('click', () => {
      dropdown.classList.toggle('hidden');
    });

    dropdown.querySelectorAll('[data-sort-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (input) input.value = btn.dataset.sortValue;
        if (label) label.textContent = btn.textContent.trim();
        dropdown.classList.add('hidden');
        this.form?.submit();
      });
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        dropdown.classList.add('hidden');
      }
    });
  }

  // ── Price range slider ──

  // Width of a thumb in px, mirrored from the CSS. Positions are inset by half
  // of it at each end so a thumb parked at the minimum or maximum still sits on
  // the rail instead of hanging off it.
  static THUMB_SIZE = 19;

  #initRangeSlider(container) {
    const minThumb = container.querySelector('[data-range-min]');
    const maxThumb = container.querySelector('[data-range-max]');
    const track = container.querySelector('[data-range-track]');
    const wrapper = container.closest('[data-price-filter]') || container.parentElement;
    const minInput = wrapper.querySelector('[data-price-min]');
    const maxInput = wrapper.querySelector('[data-price-max]');
    const minLabel = wrapper.querySelector('[data-label-min]');
    const maxLabel = wrapper.querySelector('[data-label-max]');

    if (!minThumb || !maxThumb || !track) return;

    // The ceiling is the most expensive product in this collection, handed over
    // by the section — never a hardcoded number.
    const rangeMax = parseFloat(container.dataset.rangeCeiling) || 0;
    if (rangeMax <= 0) return;

    const currencySymbol = container.dataset.rangeCurrency || '$';
    const thumb = CollectionFilters.THUMB_SIZE;
    // Keyboard nudges in ~1% steps so long ranges stay usable, minimum 1 unit.
    const step = Math.max(1, Math.round(rangeMax / 100));

    let minVal = this.#clamp(parseFloat(minInput?.value) || 0, 0, rangeMax);
    let maxVal = this.#clamp(parseFloat(maxInput?.value) || rangeMax, 0, rangeMax);
    if (minVal > maxVal) [minVal, maxVal] = [maxVal, minVal];

    // pct of the rail → CSS offset that keeps the thumb inside both ends.
    const offset = (pct) => `calc(${pct}% + ${(0.5 - pct / 100) * thumb}px)`;

    const update = () => {
      const minPct = (minVal / rangeMax) * 100;
      const maxPct = (maxVal / rangeMax) * 100;

      minThumb.style.left = offset(minPct);
      maxThumb.style.left = offset(maxPct);
      track.style.left = offset(minPct);
      track.style.right = `calc(${100 - maxPct}% - ${(0.5 - maxPct / 100) * thumb}px)`;

      // An empty value means "no bound", which keeps the parameter out of the
      // filter URL when the customer hasn't narrowed that end.
      if (minInput) minInput.value = minVal > 0 ? minVal : '';
      if (maxInput) maxInput.value = maxVal < rangeMax ? maxVal : '';

      minThumb.setAttribute('aria-valuenow', String(minVal));
      maxThumb.setAttribute('aria-valuenow', String(maxVal));

      if (minLabel) minLabel.textContent = `${currencySymbol}${minVal}`;
      if (maxLabel) {
        maxLabel.textContent = `${currencySymbol}${maxVal}${maxVal >= rangeMax ? '+' : ''}`;
      }
    };

    const valueFromEvent = (event) => {
      const rect = container.getBoundingClientRect();
      const usable = rect.width - thumb;
      if (usable <= 0) return 0;
      const pct = (event.clientX - rect.left - thumb / 2) / usable;
      return Math.round(this.#clamp(pct, 0, 1) * rangeMax);
    };

    const setMin = (value) => {
      minVal = this.#clamp(Math.min(value, maxVal), 0, rangeMax);
    };
    const setMax = (value) => {
      maxVal = this.#clamp(Math.max(value, minVal), 0, rangeMax);
    };

    let active = null;

    const onMove = (event) => {
      if (!active) return;
      const value = valueFromEvent(event);
      active === minThumb ? setMin(value) : setMax(value);
      update();
    };

    const onUp = (event) => {
      if (!active) return;
      active.classList.remove('is-dragging');
      active.releasePointerCapture?.(event.pointerId);
      active = null;
    };

    // Pressing anywhere on the rail grabs the nearer thumb, so a plain click
    // also moves the range and two thumbs sitting on the same value can still
    // be pulled apart.
    container.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      const value = valueFromEvent(event);
      const grabMin =
        Math.abs(value - minVal) < Math.abs(value - maxVal) ||
        (minVal === maxVal && value < minVal);

      active = grabMin ? minThumb : maxThumb;
      active.classList.add('is-dragging');
      active.setPointerCapture?.(event.pointerId);
      active.focus({ preventScroll: true });

      grabMin ? setMin(value) : setMax(value);
      update();
    });

    // Listening on the document keeps the drag alive when the pointer leaves
    // the rail — the customer can swing well past either end and the thumb
    // simply pins to the limit.
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);

    [minThumb, maxThumb].forEach((el) => {
      el.addEventListener('keydown', (event) => {
        const isMin = el === minThumb;
        const current = isMin ? minVal : maxVal;
        let next = current;

        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowDown':
            next = current - step;
            break;
          case 'ArrowRight':
          case 'ArrowUp':
            next = current + step;
            break;
          case 'Home':
            next = isMin ? 0 : minVal;
            break;
          case 'End':
            next = isMin ? maxVal : rangeMax;
            break;
          default:
            return;
        }

        event.preventDefault();
        isMin ? setMin(next) : setMax(next);
        update();
      });
    });

    update();
  }

  #clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }
}

customElements.define('collection-filters', CollectionFilters);

export { CollectionFilters };
